import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CustomDataTable } from '@/components/common/ui/table/CustomDataTable';
import DialogCustom from '@/components/common/ui/dialog';
import ButtonCustom from '@/components/common/ui/button';
import { ToastContext } from '@/components/common/CommonToast';
import { useLoading } from '@/components/common/loader/LoadingContext';
import { useTranslation } from '@/config/i18n';
import useFetchClient from '@/hooks/useFetchClient';
import usePagination from '@/hooks/usePagination';
import useSearch from '@/hooks/useSearch';
import { normalizeTimeZone, toastErrorMessage } from '@/lib/utils';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import { ROLE } from '@/shared/constants/user';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { Pagination } from '@/types/pagination';
import { MentorListItem } from '@/types/user';
import { MentorColumns } from '@/pages/mentor-management/table/MentorColumns';

type Props = {
  searchTrigger: number;
};

const MentorList: React.FC<Props> = ({ searchTrigger }) => {
  const [t] = useTranslation('');
  const navigate = useNavigate();
  const { apiService } = apiRequest();
  const { searchParams } = useSearch();
  const { toast } = useContext(ToastContext);
  const { setLoadingGlobal } = useLoading();
  const { query } = Object.fromEntries(searchParams.entries());
  const [dialogVisible, setDialogVisible] = useState(false);
  const [changeStatusSuccess, setChangeStatusSuccess] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState<{
    isActive: boolean;
    rowData: MentorListItem;
  }>();

  const params = useMemo(() => {
    return query
      ? undefined
      : { limit: DEFAULT_PER_PAGE, paginate: DEFAULT_PAGE, role: ROLE.MENTOR };
  }, [query]);

  const queryCustom = useMemo(() => {
    const timezone = normalizeTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    return query
      ? `${query}&role=${ROLE.MENTOR}&timezone=${timezone}`
      : `timezone=${timezone}`;
  }, [query]);

  const { data: mentors, error } = useFetchClient(
    async () => await apiService.users.query(queryCustom, params),
    [query, changeStatusSuccess, searchTrigger],
    {
      isLoadingGlobal: true,
      isScrollToTop: true,
    },
  );

  if (error) {
    toast?.current?.show(toastErrorMessage(t('common.toast.fail')));
  }

  const { paginationComputed, setPagination } = usePagination(
    mentors?.pagination,
  );

  function handleEditMentor(id: number) {
    navigate(`${PATH.mentor_management}/${PATH.mentor_management_edit}/${id}`);
  }

  function handleEditStatusMentor(isActive: boolean, rowData: MentorListItem) {
    setSelectedMentor({ isActive, rowData });
    setDialogVisible(true);
  }

  async function handleConfirmStatus() {
    if (!selectedMentor) return;

    setDialogVisible(false);
    setChangeStatusSuccess(false);
    setLoadingGlobal(true);
    const { error: userError } = await apiService.users.update(
      selectedMentor.rowData.id,
      {
        role: ROLE.MENTOR,
        status: selectedMentor.isActive ? 'active' : 'inactive',
        require_password_update: false,
      },
    );
    setLoadingGlobal(false);

    if (userError) {
      toast?.current?.show(toastErrorMessage(t('common.toast.fail')));
      return;
    }

    setChangeStatusSuccess(true);
  }

  function onSortChanged(paginationState: Pagination) {
    setPagination(paginationState);
  }

  function onPaginationChanged(paginationState: Pagination) {
    setPagination({
      ...paginationComputed,
      paginate: paginationState.paginate,
      limit: paginationState.limit,
    });
  }

  return (
    <>
      <CustomDataTable
        wrapClassName="mt-[10px]"
        values={mentors?.data ?? []}
        columns={MentorColumns(t, handleEditMentor, handleEditStatusMentor)}
        pagination={paginationComputed}
        onPaginationChanged={onPaginationChanged}
        onSortChanged={onSortChanged}
      />

      <DialogCustom
        header={t('common.dialog.header')}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        footer={
          <div className="field">
            <div className="flex justify-end">
              <ButtonCustom
                type="button"
                className="cancel-button p-button-sm mr-2"
                severity="secondary"
                onClick={() => setDialogVisible(false)}
              >
                {t('common.dialog.cancel')}
              </ButtonCustom>
              <ButtonCustom type="button" onClick={handleConfirmStatus}>
                {t('common.dialog.ok')}
              </ButtonCustom>
            </div>
          </div>
        }
      >
        {selectedMentor?.isActive
          ? t('user_list.message_on')
          : t('user_list.message_off')}
      </DialogCustom>
    </>
  );
};

export default MentorList;
